import React, { useMemo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"

import { useDeliveryLocationToggle } from "./components/useDeliveryLocationToggle"
import { ensureMeetingTimeFields } from "../form/helpers"
import { DeliveryTimesField, DynamicConfigKey, MeetingServiceConfig } from "../form/formTypes"
import { CreateOrderDialogType, ProductWithQuantity, SelectedMainUserState } from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { ExtraServices } from "./extraServices/ExtraServices"
import { GroupFields } from "./components/GroupFields"
import { IconBadge } from "../../../badges/IconBadge"
import { AccordionSection } from "./AccordionSection"

const useStyles = makeStyles()((theme) => ({
    disabledContent: {
        pointerEvents: "none",
    },
    defaultButton: {
        color: theme.palette.tailwind.blue[500],
        fontSize: "1rem",
        fontWeight: "400",
        padding: "0",
    },
}))

type Props = {
    meetingServiceConfiguration: MeetingServiceConfig
    expandedAccordionKey: string | null
    setExpandedAccordionKey: (k: string | null) => void
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
    merchantId?: number
}

export function DrawerSections({
    meetingServiceConfiguration,
    expandedAccordionKey,
    setExpandedAccordionKey,
    dialogType,
    user,
    merchantId,
}: Props) {
    const { classes } = useStyles()

    const { isSpecialOrder } = useCreateOrderDialogType(dialogType)

    const { sections } = meetingServiceConfiguration

    const visibleSections = (sections ?? []).filter(({ showInDrawer }) => showInDrawer !== false)

    const { deliveryFields, showOtherLocationField, setShowOtherLocationField, hasMeetingLocationRequired } =
        useDeliveryLocationToggle(visibleSections)

    const [{ value: deliveries }] = useField<DeliveryTimesField | undefined>(DynamicConfigKey.MULTIPLE_DELIVERIES)

    const hasMeetingTimeRequired = useMemo(() => {
        if (!deliveries?.deliveryTimes) return false
        return deliveries.deliveryTimes.some((dt) =>
            dt.orderLines.some(
                (line) => "isMeetingTimeRequired" in line && (line as ProductWithQuantity).isMeetingTimeRequired,
            ),
        )
    }, [deliveries])

    const correctedSections = useMemo(() => {
        if (!hasMeetingTimeRequired) return visibleSections
        return ensureMeetingTimeFields({ ...meetingServiceConfiguration, sections: visibleSections }).sections
    }, [visibleSections, hasMeetingTimeRequired, meetingServiceConfiguration])

    return (
        <>
            {correctedSections.map(({ title, groups }, index) => (
                <AccordionSection
                    key={`section-${index}`}
                    accordionKey={`section-${index}`}
                    expandedAccordionKey={expandedAccordionKey}
                    setExpandedAccordionKey={setExpandedAccordionKey}
                    title={title}
                    updateAccordionContentUi={isSpecialOrder ? classes.disabledContent : ""}
                    icon={<IconBadge variant="numeric" color="blue" value={index + 1} />}
                >
                    {groups.map((group, groupIndex) => {
                        const groupTitle = group.title
                        return (
                            <React.Fragment key={`section-${index}-group-${groupIndex}`}>
                                <GroupFields
                                    title={groupTitle}
                                    fields={group.fields}
                                    meetingServiceConfiguration={meetingServiceConfiguration}
                                    user={user}
                                    defaultButtonClass={classes.defaultButton}
                                    deliveryFields={deliveryFields}
                                    showOtherLocationField={showOtherLocationField}
                                    setShowOtherLocationField={setShowOtherLocationField}
                                    hasMeetingTimeRequired={hasMeetingTimeRequired}
                                    hasMeetingLocationRequired={hasMeetingLocationRequired}
                                />
                            </React.Fragment>
                        )
                    })}
                    {index === 1 && (
                        <ExtraServices
                            userId={user.userId}
                            merchantId={merchantId || 0}
                            dialogType={dialogType}
                            onAutoExpand={() => setExpandedAccordionKey("section-1")}
                        />
                    )}
                </AccordionSection>
            ))}
        </>
    )
}
